<script setup lang="ts">
/**
 * Aura AGM — DDF: Directors' Declaration Form (Disclosure of Interest, CA 1967 s.156 & s.165).
 * 이해관계 구분은 [[if own_name_interests]] … 반복 섹션. 플래그가 payload 에 없으면 조건이 거짓으로
 * 평가돼 표가 통째로 삭제되므로, 화면 로컬이 아니라 반드시 fill(문서 값)에 기록한다.
 */
import { inject, onMounted, ref, watch } from 'vue';
import CddVarField from '../cdd/CddVarField.vue';
import AgmSigners from '../cdd/AgmSigners.vue';
import { useAgmFill, DOC_SCOPE } from '../cdd/useCddFill';
import './agmDoc.css';

const fill = useAgmFill();
const scope = inject(DOC_SCOPE, '');
const K = (k: string) => scope + k;

const FLAG_KEYS = [
  'other_interests',
  'own_name_interests',
  'family_interests',
  'deemed_interests',
  'corporate_interests',
] as const;
// s.165 이해관계 4구분은 **기본 체크** — 원본 종이 양식이 4개 구분에 모두 기재란을 두고
//   'N.B. Please indicate NIL if no interest to be disclosed' 를 안내하기 때문. 미체크면
//   Word 템플릿의 [[if …_interests]] 행이 삭제돼 라벨만 남은 '반쪽 표'가 나온다(고객 리포트).
//   해당 없는 구분은 운영자가 해제하면 된다. s.156 법인/상호(other_interests)도 기본 체크 — 미체크면 표 행이 지워져
//   박스가 빠진다(고객 2026-11-04 Inspire Brands). Incorporation DDF 와 같은 기본값.
const DEFAULTS: Record<string, boolean> = {
  other_interests: true,
  own_name_interests: true,
  family_interests: true,
  deemed_interests: true,
  corporate_interests: true,
};
const flags = ref<Record<string, boolean>>({ ...DEFAULTS });

function readFlag(k: string): boolean {
  const v = String(fill.values[K(k)] ?? '').trim().toLowerCase();
  if (!v) return DEFAULTS[k];
  return v === 'yes' || v === 'true' || v === '1';
}
function syncFromFill() {
  for (const k of FLAG_KEYS) flags.value[k] = readFlag(k);
}
function toggle(k: string) {
  flags.value[k] = !flags.value[k];
  fill.setValue(K(k), flags.value[k] ? 'yes' : 'no');
}
onMounted(() => {
  for (const k of FLAG_KEYS) {
    if (!String(fill.values[K(k)] ?? '').trim()) fill.setValue(K(k), DEFAULTS[k] ? 'yes' : 'no');
  }
  syncFromFill();
});
watch(() => FLAG_KEYS.map((k) => fill.values[K(k)]).join('|'), () => syncFromFill());

// ⚠ 4개 구분은 서로 다른 보유자·수량을 **여러 건** 기재할 수 있다 → 섹션별 구분 키(pfx) + **다건(행)**.
//   normalize 가 own_name_${i}_registered_holder … 를 own_name_interests 루프의 배열 요소로 매핑.
const INTEREST_SECTIONS = [
  { key: 'own_name_interests', pfx: 'own_name', label: 'Interest registered in own name' },
  { key: 'family_interests', pfx: 'family', label: 'Interest of immediate family members' },
  { key: 'deemed_interests', pfx: 'deemed', label: 'Other deemed interest' },
  { key: 'corporate_interests', pfx: 'corporate', label: 'In name of Bodies Corporate' },
];
const ROW_FIELDS = ['registered_holder', 'interest_description', 'amount_at_appointment', 'amount_at_end', 'date_price_details'];

// ── 다건 행 관리 (구분당 N행) ──
function countOf(pfx: string): number {
  const n = parseInt(String(fill.values[K(`${pfx}_count`)] ?? ''), 10);
  return Number.isFinite(n) && n > 0 ? n : 1;
}
function rowsOf(pfx: string): number[] { return Array.from({ length: countOf(pfx) }, (_, i) => i + 1); }
function addRow(pfx: string) { fill.setValue(K(`${pfx}_count`), String(countOf(pfx) + 1)); }
function removeRow(pfx: string, idx: number, fields: string[]) {
  const n = countOf(pfx);
  for (let j = idx; j < n; j++) for (const f of fields) fill.setValue(K(`${pfx}_${j}_${f}`), fill.getValue(K(`${pfx}_${j + 1}_${f}`)));
  for (const f of fields) fill.setValue(K(`${pfx}_${n}_${f}`), '');
  fill.setValue(K(`${pfx}_count`), String(Math.max(1, n - 1)));
}
</script>

<template>
  <div class="agm-doc">
    <p class="doc-p">To: The Board of Directors<br /><CddVarField field-key="company_name" label="Company Name" /> ("The Company")</p>
    <div class="doc-title">GENERAL NOTICE OF DISCLOSURE OF INTEREST</div>
    <p class="doc-p">
      In accordance with Section 156 of the Singapore Companies Act 1967, I hereby give general notice that I am also a director or
      member or Chief Executive Officer of the corporation or firms set out below and I am to be regarded as interested in any
      transaction/s which may, after the date of this notice, be made with such corporations or firms.
    </p>
    <p class="doc-p">
      <span class="doc-toggle" :class="{ off: !flags.other_interests }" @click="toggle('other_interests')">{{ flags.other_interests ? '☑' : '☐' }} Corporations / firms</span>
    </p>
    <table v-if="flags.other_interests" class="cdd-tbl">
      <thead><tr><th>Name of Corporation / Firm</th><th>Nature of Interest(s) (Director / Member / Partner / Officer)</th><th></th></tr></thead>
      <tbody>
        <tr v-for="i in rowsOf('other')" :key="i">
          <td><CddVarField :field-key="`other_${i}_corporation_name`" label="Corporation Name" /></td>
          <td><CddVarField :field-key="`other_${i}_interest_nature`" label="Interest Nature" /></td>
          <td style="width:24px;text-align:center"><button v-if="countOf('other') > 1" type="button" class="list-rm" title="Remove" @click="removeRow('other', i, ['corporation_name', 'interest_nature'])">×</button></td>
        </tr>
      </tbody>
    </table>
    <button v-if="flags.other_interests" type="button" class="list-add" @click="addRow('other')">+ Add corporation / firm</button>

    <div class="doc-title" style="margin-top:14px">NOTICE OF DIRECTORS' / CEO'S INTEREST IN THE COMPANY AND ITS RELATED CORPORATIONS</div>
    <p class="doc-p">
      In accordance with Section 165 of the Singapore Companies Act 1967, I hereby notify you of the changes in particulars of my
      interests in the shares, debentures, rights, options and contracts relating to shares of the company and/or its related
      corporation/s on the date mentioned below.
    </p>

    <template v-for="s in INTEREST_SECTIONS" :key="s.key">
      <p class="doc-p">
        <span class="doc-toggle" :class="{ off: !flags[s.key] }" @click="toggle(s.key)">{{ flags[s.key] ? '☑' : '☐' }} {{ s.label }}</span>
      </p>
      <table v-if="flags[s.key]" class="cdd-tbl">
        <thead><tr><th>Name of Registered Holder</th><th>Description of Interests</th><th>Amount at Date of Appointment / Balance at Beginning of Financial Year / Period <CddVarField field-key="financial_year_start_date" label="FY Start Date" /></th><th>Balance at End of Financial Year / Period <CddVarField field-key="financial_year_end" label="FY End Date" /></th><th>Date Acquired / Disposed &amp; Price</th><th></th></tr></thead>
        <tbody>
          <tr v-for="i in rowsOf(s.pfx)" :key="i">
            <td><CddVarField :field-key="`${s.pfx}_${i}_registered_holder`" label="Registered Holder" /></td>
            <td><CddVarField :field-key="`${s.pfx}_${i}_interest_description`" label="Interest Description" /></td>
            <td><CddVarField :field-key="`${s.pfx}_${i}_amount_at_appointment`" label="Amount at Appointment" /></td>
            <td><CddVarField :field-key="`${s.pfx}_${i}_amount_at_end`" label="Amount at End" /></td>
            <td><CddVarField :field-key="`${s.pfx}_${i}_date_price_details`" label="Date / Price" /></td>
            <td style="width:24px;text-align:center"><button v-if="countOf(s.pfx) > 1" type="button" class="list-rm" title="Remove" @click="removeRow(s.pfx, i, ROW_FIELDS)">×</button></td>
          </tr>
        </tbody>
      </table>
      <button v-if="flags[s.key]" type="button" class="list-add" @click="addRow(s.pfx)">+ Add interest</button>
    </template>

    <p class="doc-note">N.B. Please indicate 'NIL' if no interest to be disclosed.</p>
    <p class="doc-p" style="margin-top:10px"><b>Name of Director / Chief Executive Officer:</b> <CddVarField field-key="director_name" label="Director Name" /></p>
    <p class="doc-p"><b>Signature:</b></p>
    <AgmSigners var-key="ddf_Signers" board-label="Director" />
    <p class="doc-p"><b>Date:</b> <CddVarField field-key="resolution_date" label="Date" /></p>
  </div>
</template>
